export type Dimensions = {
  width: number;
  height: number;
};

export type Coordinates = {
  x: number;
  y: number;
};

export enum AnimationType {
  IN = 'in',
  OUT = 'out',
}

export type Particle = {
  initialCoordinates: Coordinates;
  currentPosition: Coordinates;
  targetCoordinates: Coordinates;
  radius: number;
  emittedEffect: boolean;
};

export interface EffectParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  startTime: number;
  lifetime: number;
  radius: number;
  colorProgression: Array<string>;
  progress: number;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
}

export type ParticleRendererProps = {
  radius: number;
  duration: number;
  animationType: AnimationType;
  direction: number;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
  windDirection: number;
  windSpeed: number;
  turbulence: number;
  particleColor1: string;
  particleColor2: string;
  particleColor3: string;
  particleColor4: string;
  particleColorCount: number;
};

export type InstanceState = {
  particles: Array<Particle>;
  animationFrameId: number;
  frameCanvas: OffscreenCanvas;
  frameContext: OffscreenCanvasRenderingContext2D;
  mainCanvas: OffscreenCanvas;
  mainContext: ImageBitmapRenderingContext;
  imageData: ImageData;
  imageBitmap: ImageBitmap;
  particleRendererProps: ParticleRendererProps;
  textBoundaries: TextBoundaries;
  effectParticles: Array<EffectParticle>;
  animationProgress: number;
  animationState: AnimationState;
};

export interface WindowWithWorker extends Window {
  particlesAnimationWorker?: Worker;
}

export const INITIALIZE = 'INITIALIZE' as const;
export const PLAY = 'PLAY' as const;
export const STOP = 'STOP' as const;
export const PAUSE = 'PAUSE' as const;
export const UPDATE_IMAGE = 'UPDATE_IMAGE' as const;
export const TERMINATE_INSTANCE = 'TERMINATE_INSTANCE' as const;
export const SET_PREVIEW = 'SET_PREVIEW' as const;
export const UPDATE_RENDERER_PROPS = 'UPDATE_RENDERER_PROPS' as const;

export const ANIMATION_FINISHED = 'ANIMATION_FINISHED' as const;
export const INITIALIZED = 'INITIALIZED' as const;
export const ANIMATION_STOPPED = 'ANIMATION_STOPPED' as const;

export type InitializeMessagePayload = {
  imageBitmap: ImageBitmap;
  canvas: OffscreenCanvas;
  particleRendererProps: ParticleRendererProps;
};

export type UpdateImageMessagePayload = {
  imageBitmap: ImageBitmap;
};

export type UpdateRendererPropsPayload = ParticleRendererProps;

type MessageType =
  | typeof INITIALIZE
  | typeof PLAY
  | typeof STOP
  | typeof PAUSE
  | typeof UPDATE_IMAGE
  | typeof TERMINATE_INSTANCE
  | typeof ANIMATION_FINISHED
  | typeof INITIALIZED
  | typeof SET_PREVIEW
  | typeof UPDATE_RENDERER_PROPS
  | typeof ANIMATION_STOPPED;

export type MessagePayloadMap = {
  [INITIALIZE]: InitializeMessagePayload;
  [PLAY]: undefined;
  [STOP]: undefined;
  [PAUSE]: undefined;
  [UPDATE_IMAGE]: UpdateImageMessagePayload;
  [TERMINATE_INSTANCE]: undefined;
  [ANIMATION_FINISHED]: undefined;
  [INITIALIZED]: undefined;
  [SET_PREVIEW]: undefined;
  [UPDATE_RENDERER_PROPS]: UpdateRendererPropsPayload;
  [ANIMATION_STOPPED]: undefined;
};

export type Message<T extends MessageType> = {
  type: T;
  payload: MessagePayloadMap[T];
  componentId: string;
};

export type MainThreadMessage =
  | Message<typeof INITIALIZE>
  | Message<typeof PLAY>
  | Message<typeof STOP>
  | Message<typeof PAUSE>
  | Message<typeof UPDATE_IMAGE>
  | Message<typeof TERMINATE_INSTANCE>
  | Message<typeof SET_PREVIEW>
  | Message<typeof UPDATE_RENDERER_PROPS>;

export type WorkerMessageTypes =
  | typeof ANIMATION_FINISHED
  | typeof INITIALIZED
  | typeof ANIMATION_STOPPED;

export type WorkerMessage =
  | Message<typeof ANIMATION_FINISHED>
  | Message<typeof INITIALIZED>;

export type AnimationState =
  | 'playing'
  | 'stopped'
  | 'preview'
  | 'finished'
  | 'paused';

export type TextBoundaries = Dimensions & {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};
